import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { 
  CheckCircle, XCircle, Loader2, Clock, ArrowRight, 
  BookOpen, Mail, RefreshCw, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FutureSkillsPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed, pending
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [countdown, setCountdown] = useState(null);

  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (orderId) {
      verifyPayment();
    } else {
      setStatus('failed');
      setError('Invalid payment callback - missing order ID');
    }
  }, [orderId]);

  // Auto-redirect countdown
  useEffect(() => {
    if (status === 'success' || status === 'failed') {
      setCountdown(5);
    }
  }, [status]);

  useEffect(() => {
    if (countdown === null || countdown <= 0) return;
    const timer = setTimeout(() => {
      if (countdown === 1) {
        navigate('/codearena/dashboard');
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown, navigate]);

  const verifyPayment = async () => {
    setStatus('verifying');
    try {
      const res = await axios.get(`${API}/future-skills/payment-status/${orderId}`);
      setEnrollment(res.data);
      
      if (res.data.status === 'confirmed' || res.data.status === 'completed') {
        setStatus('success');
      } else if (res.data.status === 'payment_failed') {
        setStatus('failed');
      } else if (res.data.status === 'pending_payment') {
        setStatus('pending');
        // Auto-retry up to 3 times with 3-second intervals
        if (retryCount < 3) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            verifyPayment();
          }, 3000);
        }
      }
    } catch (err) {
      console.error('Payment verification error:', err);
      setStatus('failed');
      setError(err.response?.data?.detail || 'Failed to verify payment');
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    verifyPayment();
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-cyan-500/10 to-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-gradient-to-br from-purple-500/10 to-pink-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-md w-full">
        {/* Verifying State */}
        {status === 'verifying' && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-testid="payment-verifying">
            <div className="w-20 h-20 mx-auto mb-6 bg-cyan-500/20 rounded-full flex items-center justify-center">
              <Loader2 className="text-cyan-400 animate-spin" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verifying Payment</h1>
            <p className="text-gray-400 mb-4">Please wait while we confirm your payment...</p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <RefreshCw size={14} className="animate-spin" />
              <span>Checking payment status{retryCount > 0 ? ` (attempt ${retryCount + 1})` : ''}...</span>
            </div>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && enrollment && (
          <div className="bg-white/5 border border-green-500/30 rounded-2xl p-8 text-center" data-testid="payment-success">
            <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-400" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-400 mb-6">
              You have been successfully enrolled in the course.
            </p>
            
            {/* Enrollment Details */}
            <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
              <h3 className="text-sm text-gray-400 mb-3">Enrollment Details</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Course</span>
                  <span className="text-white font-medium text-right max-w-[200px] truncate">{enrollment.course_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount Paid</span>
                  <span className="text-green-400 font-medium">
                    ₹{enrollment.amount?.toLocaleString('en-IN')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Order ID</span>
                  <span className="text-gray-300 text-sm font-mono">{orderId}</span>
                </div>
              </div>
            </div>
            
            {/* Email Notice */}
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-6 justify-center">
              <Mail size={14} />
              <span>Confirmation sent to {enrollment.student_email}</span>
            </div>
            
            {/* Action Buttons */}
            <div className="space-y-3">
              <Link
                to="/codearena/dashboard"
                className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
                data-testid="go-to-dashboard-btn"
              >
                <BookOpen size={20} />
                Go to Dashboard
              </Link>
              <Link
                to="/future-skills/courses"
                className="flex items-center justify-center gap-2 w-full py-3 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
              >
                Browse More Courses
                <ArrowRight size={18} />
              </Link>
              {countdown !== null && countdown > 0 && (
                <p className="text-sm text-center text-gray-500 mt-1" data-testid="redirect-countdown">
                  Redirecting to dashboard in {countdown}s...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Pending State */}
        {status === 'pending' && (
          <div className="bg-white/5 border border-amber-500/30 rounded-2xl p-8 text-center" data-testid="payment-pending">
            <div className="w-20 h-20 mx-auto mb-6 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Clock className="text-amber-400" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Pending</h1>
            <p className="text-gray-400 mb-6">
              Your payment is being processed. This may take a few moments.
            </p>
            
            {enrollment && (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Course</span>
                    <span className="text-white">{enrollment.course_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Amount</span>
                    <span className="text-amber-400">₹{enrollment.amount?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="flex items-center justify-center gap-2 w-full py-3 bg-amber-500/20 text-amber-400 rounded-xl font-medium hover:bg-amber-500/30 transition-colors"
                data-testid="retry-verification-btn"
              >
                <RefreshCw size={18} />
                Check Again
              </button>
              <p className="text-xs text-gray-500">
                If your payment was debited, it will be verified automatically. If not confirmed within 24 hours, please contact support.
              </p>
            </div>
          </div>
        )}

        {/* Failed State */}
        {status === 'failed' && (
          <div className="bg-white/5 border border-red-500/30 rounded-2xl p-8 text-center" data-testid="payment-failed">
            <div className="w-20 h-20 mx-auto mb-6 bg-red-500/20 rounded-full flex items-center justify-center">
              <XCircle className="text-red-400" size={40} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Payment Failed</h1>
            <p className="text-gray-400 mb-6">
              {error || 'We couldn\'t verify your payment. Please try again.'}
            </p>
            
            {enrollment && (
              <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Course</span>
                    <span className="text-white">{enrollment.course_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Order ID</span>
                    <span className="text-gray-300 font-mono text-xs">{orderId}</span>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 p-3 rounded-lg mb-6">
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
              <span className="text-left">
                If money was deducted from your account, it will be refunded within 5-7 business days.
              </span>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/codearena/dashboard')}
                className="flex items-center justify-center gap-2 w-full py-4 bg-gradient-to-r from-cyan-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
                data-testid="try-again-btn"
              >
                Go to Dashboard
              </button>
              <Link
                to="/future-skills/courses"
                className="flex items-center justify-center gap-2 w-full py-3 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
              >
                Browse Courses
              </Link>
              {countdown !== null && countdown > 0 && (
                <p className="text-sm text-center text-gray-500 mt-1" data-testid="redirect-countdown-fail">
                  Redirecting to dashboard in {countdown}s...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Support Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Need help? Contact us at{' '}
            <a href="mailto:support@evalentx.com" className="text-cyan-400 hover:underline">
              support@evalentx.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FutureSkillsPaymentCallback;
